/**
 * §8.1 Hero — "Learn Everything About Kalki Ai Cloud".
 * Left: title, subtitle, CTAs, quick chips.
 * Right: animated documentation hub mock — search bar with matching
 * results and floating category chips.
 */

import React from 'react'
import {
  BookOpen, LifeBuoy, Search, Sparkles, ArrowRight, FileText,
  PlayCircle, Terminal, HelpCircle, GraduationCap,
} from 'lucide-react'
import {
  Container, Eyebrow, GradientButton, GhostButton,
} from '@/components/landing/design-system'

const RESULTS = [
  { i: BookOpen,        l: 'Getting Started — Provision your first workspace', cat: 'Docs' },
  { i: PlayCircle,      l: 'Terminal tour — orders, book and hotkeys',           cat: 'Video · 11:20' },
  { i: FileText,         l: 'Strategy DSL reference',                              cat: 'Guide' },
  { i: HelpCircle,       l: 'Why is the mentor grounded?',                          cat: 'KB' },
  { i: Terminal,          l: 'Authentication — OAuth2 + tenant tokens',              cat: 'API' },
]
const CHIPS = ['Documentation', 'Guides', 'Videos', 'API', 'FAQs', 'Support']

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.14), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow>Resources</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Learn everything about{' '}
              <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Kalki Ai Cloud.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Explore documentation, implementation guides, tutorials, release notes, videos and
              product resources to get the most from Kalki Ai Cloud — whether you&apos;re evaluating,
              onboarding or scaling a live white-label platform.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#documentation" size="lg">
                <BookOpen className="h-4 w-4" /> Browse documentation
              </GradientButton>
              <GhostButton href="#support" size="lg" trailingIcon>
                <LifeBuoy className="h-4 w-4" /> Contact support
              </GhostButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {['120+ docs', '40+ videos', '250+ KB articles', '9 product guides'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <Sparkles className="h-3 w-3 text-brand-gold" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — documentation hub mock */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />

              {/* search bar */}
              <div className="relative flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5">
                <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                <input
                  readOnly
                  defaultValue="how do I brand my platform?"
                  className="flex-1 bg-transparent text-[12.5px] text-zinc-200 outline-none placeholder-zinc-500"
                />
                <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[9.5px] font-mono text-zinc-500">
                  <kbd>⌘</kbd><kbd>K</kbd>
                </span>
              </div>

              {/* category chips */}
              <div className="relative mt-3 flex flex-wrap gap-1.5">
                {CHIPS.map((c, i) => (
                  <span key={c} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                    i === 1 ? 'border-brand-gold/40 bg-brand-gold/10 text-brand-gold' :
                              'border-white/10 bg-white/[0.03] text-zinc-400'
                  }`}>{c}</span>
                ))}
              </div>

              {/* results */}
              <div className="relative mt-4 rounded-lg border border-white/10 bg-black/40 p-3">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-2">5 results</div>
                <div className="flex flex-col gap-1">
                  {RESULTS.map((r, i) => (
                    <div key={r.l} className={`flex items-start gap-2.5 rounded-md px-2 py-2 ${
                      i === 0 ? 'bg-white/[0.04] border border-white/10' : 'hover:bg-white/[0.03] border border-transparent'
                    }`}>
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-brand-gold shrink-0">
                        <r.i strokeWidth={1.8} style={{ width: 12, height: 12 }} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[12px] font-semibold ${i === 0 ? 'text-white' : 'text-zinc-200'} truncate`}>{r.l}</div>
                        <div className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">{r.cat}</div>
                      </div>
                      {i === 0 && (
                        <span className="inline-flex items-center gap-1 text-[9.5px] font-mono text-emerald-400 whitespace-nowrap">
                          <ArrowRight className="h-3 w-3" /> open
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* footer strip */}
              <div className="relative mt-3 flex items-center justify-between rounded-md border border-emerald-400/20 bg-emerald-400/[0.05] px-3 py-2">
                <div className="flex items-center gap-2 text-[10.5px] text-emerald-300">
                  <Sparkles className="h-3 w-3 text-emerald-400" />
                  <span><span className="text-white font-semibold">Predictive search</span> · ranked across every resource type</span>
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-emerald-400 font-mono">
                  ~90ms <GraduationCap className="h-3 w-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
